/**
 * Class Enrollment Management Page
 * Allows teachers to enroll/unenroll learners into classes
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, Alert, CircularProgress,
  Breadcrumbs, Link as MuiLink, Card, CardContent,
  List, ListItem, ListItemButton, ListItemText, ListItemSecondaryAction,
  Checkbox, IconButton, Stack, Chip, Divider, TextField, InputAdornment
} from '@mui/material';
import {
  Home, School, PersonAdd, PersonRemove, Search, Check, Clear, Print
} from '@mui/icons-material';
import axios from 'axios';

interface Learner {
  id: number;
  student_id: string;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
}

interface ClassInfo {
  class_id: number;
  class_name: string;
  grade_level: number;
  subject: string;
  total_learners: number;
}

interface EnrollmentData {
  class_id: number;
  class_name: string;
  grade_level: number;
  subject: string;
  total_learners: number;
  learners: Learner[];
}

const ClassEnrollmentPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Data state
  const [classInfo, setClassInfo] = useState<ClassInfo | null>(null);
  const [enrolledLearners, setEnrolledLearners] = useState<Learner[]>([]);
  const [availableLearners, setAvailableLearners] = useState<Learner[]>([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Selection state
  const [selectedEnrolled, setSelectedEnrolled] = useState<Set<number>>(new Set());
  const [selectedAvailable, setSelectedAvailable] = useState<Set<number>>(new Set());

  // Search state
  const [enrolledSearch, setEnrolledSearch] = useState('');
  const [availableSearch, setAvailableSearch] = useState('');

  // Load class enrollment data
  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);

        // Fetch enrolled learners
        const enrollmentResponse = await axios.get<EnrollmentData>(
          `/api/school-classes/${id}/enrollments/`
        );

        setClassInfo({
          class_id: enrollmentResponse.data.class_id,
          class_name: enrollmentResponse.data.class_name,
          grade_level: enrollmentResponse.data.grade_level,
          subject: enrollmentResponse.data.subject,
          total_learners: enrollmentResponse.data.total_learners,
        });
        setEnrolledLearners(enrollmentResponse.data.learners);

        // Fetch all learners from the school
        const learnersResponse = await axios.get('/api/learners/');
        const allLearners = learnersResponse.data.results || learnersResponse.data;

        // Filter out already enrolled learners
        const enrolledIds = new Set(enrollmentResponse.data.learners.map(l => l.id));
        const available = allLearners.filter((l: Learner) => !enrolledIds.has(l.id));
        setAvailableLearners(available);

      } catch (err: any) {
        console.error('Error loading data:', err);
        setError(err.response?.data?.error || 'Failed to load enrollment data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // Handle enroll learners
  const handleEnroll = async () => {
    if (selectedAvailable.size === 0) {
      setError('Please select at least one learner to enroll');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const response = await axios.post(`/api/school-classes/${id}/enroll-learners/`, {
        learner_ids: Array.from(selectedAvailable)
      });

      setSuccess(response.data.message);

      // Move learners from available to enrolled
      const learnersToMove = availableLearners.filter(l => selectedAvailable.has(l.id));
      setEnrolledLearners([...enrolledLearners, ...learnersToMove]);
      setAvailableLearners(availableLearners.filter(l => !selectedAvailable.has(l.id)));
      setSelectedAvailable(new Set());

      // Update class info
      if (classInfo) {
        setClassInfo({
          ...classInfo,
          total_learners: classInfo.total_learners + learnersToMove.length
        });
      }

    } catch (err: any) {
      console.error('Error enrolling learners:', err);
      setError(err.response?.data?.message || 'Failed to enroll learners');
    } finally {
      setSaving(false);
    }
  };

  // Handle unenroll learners
  const handleUnenroll = async () => {
    if (selectedEnrolled.size === 0) {
      setError('Please select at least one learner to remove');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const response = await axios.post(`/api/school-classes/${id}/unenroll-learners/`, {
        learner_ids: Array.from(selectedEnrolled)
      });

      setSuccess(response.data.message);

      // Move learners from enrolled to available
      const learnersToMove = enrolledLearners.filter(l => selectedEnrolled.has(l.id));
      setAvailableLearners([...availableLearners, ...learnersToMove].sort((a, b) =>
        a.last_name.localeCompare(b.last_name)
      ));
      setEnrolledLearners(enrolledLearners.filter(l => !selectedEnrolled.has(l.id)));
      setSelectedEnrolled(new Set());

      // Update class info
      if (classInfo) {
        setClassInfo({
          ...classInfo,
          total_learners: classInfo.total_learners - learnersToMove.length
        });
      }

    } catch (err: any) {
      console.error('Error removing learners:', err);
      setError(err.response?.data?.message || 'Failed to remove learners');
    } finally {
      setSaving(false);
    }
  };

  // Toggle selection
  const toggleEnrolled = (learnerId: number) => {
    const newSet = new Set(selectedEnrolled);
    if (newSet.has(learnerId)) {
      newSet.delete(learnerId);
    } else {
      newSet.add(learnerId);
    }
    setSelectedEnrolled(newSet);
  };

  const toggleAvailable = (learnerId: number) => {
    const newSet = new Set(selectedAvailable);
    if (newSet.has(learnerId)) {
      newSet.delete(learnerId);
    } else {
      newSet.add(learnerId);
    }
    setSelectedAvailable(newSet);
  };

  // Select/deselect all
  const selectAllEnrolled = () => {
    setSelectedEnrolled(new Set(filteredEnrolled.map(l => l.id)));
  };

  const deselectAllEnrolled = () => {
    setSelectedEnrolled(new Set());
  };

  const selectAllAvailable = () => {
    setSelectedAvailable(new Set(filteredAvailable.map(l => l.id)));
  };

  const deselectAllAvailable = () => {
    setSelectedAvailable(new Set());
  };

  // Print class list
  const handlePrintClassList = () => {
    if (!classInfo) return;

    // Sort enrolled learners by last name
    const sortedLearners = [...enrolledLearners].sort((a, b) =>
      a.last_name.localeCompare(b.last_name)
    );

    // Count by gender (if we have that data - for now we'll skip this)
    const now = new Date();
    const timestamp = now.toLocaleString('en-ZA', {
      hour: '2-digit',
      minute: '2-digit',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });

    // Create print content
    const printContent = `
      <html>
        <head>
          <title>Class List - ${classInfo.class_name}</title>
          <style>
            @media print {
              @page { margin: 2cm; }
            }
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
              max-width: 800px;
              margin: 0 auto;
            }
            h1 {
              font-size: 18px;
              margin-bottom: 5px;
              font-weight: bold;
            }
            h2 {
              font-size: 16px;
              margin: 5px 0;
              font-weight: normal;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 20px 0;
            }
            th {
              text-align: left;
              padding: 8px;
              border-bottom: 1px solid #000;
              font-weight: bold;
            }
            td {
              padding: 8px;
              border-bottom: 1px solid #ddd;
            }
            .footer {
              margin-top: 20px;
              font-size: 14px;
            }
          </style>
        </head>
        <body>
          <h1>School Name</h1>
          <h2>Scratch List</h2>
          <h2>${classInfo.subject} Graad ${classInfo.grade_level} - ${classInfo.class_name}</h2>

          <table>
            <thead>
              <tr>
                <th>Last Name</th>
                <th>Preferred Name</th>
              </tr>
            </thead>
            <tbody>
              ${sortedLearners.map(learner => `
                <tr>
                  <td>${learner.last_name}</td>
                  <td>${learner.first_name}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          <div class="footer">
            Total: ${sortedLearners.length} people, generated at ${timestamp}
          </div>
        </body>
      </html>
    `;

    // Open print window
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
      }, 250);
    }
  };

  // Filter learners by search
  const filteredEnrolled = enrolledLearners.filter(l =>
    (l.full_name || '').toLowerCase().includes(enrolledSearch.toLowerCase()) ||
    (l.student_id || '').toLowerCase().includes(enrolledSearch.toLowerCase())
  );

  const filteredAvailable = availableLearners.filter(l =>
    (l.full_name || '').toLowerCase().includes(availableSearch.toLowerCase()) ||
    (l.student_id || '').toLowerCase().includes(availableSearch.toLowerCase())
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!classInfo) {
    return (
      <Box>
        <Alert severity="error">Class not found</Alert>
        <Button onClick={() => navigate('/classes')} sx={{ mt: 2 }}>
          Back to Classes
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <MuiLink
          component="button"
          variant="body1"
          onClick={() => navigate('/dashboard')}
          sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }}
        >
          <Home fontSize="small" />
          Dashboard
        </MuiLink>
        <MuiLink
          component="button"
          variant="body1"
          onClick={() => navigate('/classes')}
          sx={{ cursor: 'pointer' }}
        >
          Classes
        </MuiLink>
        <Typography color="text.primary">
          Manage Enrollment: {classInfo.class_name}
        </Typography>
      </Breadcrumbs>

      {/* Error/Success Alerts */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="start">
          <Box>
            <Typography variant="h4" gutterBottom>
              <School sx={{ mr: 1, verticalAlign: 'middle' }} />
              Manage Class Enrollment
            </Typography>
            <Typography variant="h5" color="primary" gutterBottom>
              {classInfo.class_name}
            </Typography>
            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
              <Chip label={`Grade ${classInfo.grade_level}`} color="primary" variant="outlined" />
              <Chip label={classInfo.subject} color="secondary" variant="outlined" />
              <Chip label={`${classInfo.total_learners} enrolled`} color="success" />
            </Stack>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Print />}
              onClick={handlePrintClassList}
              disabled={enrolledLearners.length === 0}
            >
              Print Class List
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/classes')}
            >
              Back to Classes
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* Enrollment Management */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
        {/* Enrolled Learners */}
        <Box flex={1}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Enrolled Learners ({filteredEnrolled.length})
                </Typography>
                <Stack direction="row" spacing={1}>
                  <IconButton size="small" onClick={selectAllEnrolled} title="Select All">
                    <Check fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={deselectAllEnrolled} title="Deselect All">
                    <Clear fontSize="small" />
                  </IconButton>
                </Stack>
              </Box>

              <TextField
                fullWidth
                size="small"
                placeholder="Search enrolled learners..."
                value={enrolledSearch}
                onChange={(e) => setEnrolledSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              <Box sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                {filteredEnrolled.length === 0 ? (
                  <Box p={3} textAlign="center">
                    <Typography color="textSecondary">
                      {enrolledSearch ? 'No learners match your search' : 'No learners enrolled yet'}
                    </Typography>
                  </Box>
                ) : (
                  <List dense>
                    {filteredEnrolled.map((learner) => (
                      <ListItem key={learner.id} disablePadding>
                        <ListItemButton
                          onClick={() => toggleEnrolled(learner.id)}
                          selected={selectedEnrolled.has(learner.id)}
                        >
                          <Checkbox
                            edge="start"
                            checked={selectedEnrolled.has(learner.id)}
                            tabIndex={-1}
                            disableRipple
                          />
                          <ListItemText
                            primary={learner.full_name}
                            secondary={`ID: ${learner.student_id}`}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>

              <Button
                fullWidth
                variant="outlined"
                color="error"
                startIcon={<PersonRemove />}
                onClick={handleUnenroll}
                disabled={selectedEnrolled.size === 0 || saving}
                sx={{ mt: 2 }}
              >
                Remove Selected ({selectedEnrolled.size})
              </Button>
            </CardContent>
          </Card>
        </Box>

        {/* Available Learners */}
        <Box flex={1}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Available Learners ({filteredAvailable.length})
                </Typography>
                <Stack direction="row" spacing={1}>
                  <IconButton size="small" onClick={selectAllAvailable} title="Select All">
                    <Check fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={deselectAllAvailable} title="Deselect All">
                    <Clear fontSize="small" />
                  </IconButton>
                </Stack>
              </Box>

              <TextField
                fullWidth
                size="small"
                placeholder="Search available learners..."
                value={availableSearch}
                onChange={(e) => setAvailableSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              <Box sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                {filteredAvailable.length === 0 ? (
                  <Box p={3} textAlign="center">
                    <Typography color="textSecondary">
                      {availableSearch ? 'No learners match your search' : 'All learners are enrolled'}
                    </Typography>
                  </Box>
                ) : (
                  <List dense>
                    {filteredAvailable.map((learner) => (
                      <ListItem key={learner.id} disablePadding>
                        <ListItemButton
                          onClick={() => toggleAvailable(learner.id)}
                          selected={selectedAvailable.has(learner.id)}
                        >
                          <Checkbox
                            edge="start"
                            checked={selectedAvailable.has(learner.id)}
                            tabIndex={-1}
                            disableRipple
                          />
                          <ListItemText
                            primary={learner.full_name}
                            secondary={`ID: ${learner.student_id}`}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>

              <Button
                fullWidth
                variant="contained"
                color="primary"
                startIcon={<PersonAdd />}
                onClick={handleEnroll}
                disabled={selectedAvailable.size === 0 || saving}
                sx={{ mt: 2 }}
              >
                Enroll Selected ({selectedAvailable.size})
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Stack>
    </Box>
  );
};

export default ClassEnrollmentPage;
